import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminDashboard, manualRefresh } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, Target, Zap, Building2, GitMerge, RefreshCw,
  TrendingUp, Users, Database, Clock, Activity, Bell, ChevronRight
} from 'lucide-react';
import { formatRelative } from '@/lib/formatters';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    fetchAdminDashboard().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await manualRefresh();
      load();
    } catch { /* ignore */ }
    setRefreshing(false);
  };

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { stats, recent_syncs, cache } = data;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
          Dashboard
        </h1>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        <StatCard icon={<BarChart3 />} label="Matches" value={stats.matches} sub={`${stats.prematch} prematch`} color="blue" />
        <StatCard icon={<Database />} label="Odds" value={stats.odds} color="slate" />
        <StatCard icon={<Target />} label="Value Bets" value={stats.value_bets} color="amber" />
        <StatCard icon={<Zap />} label="Surebets" value={stats.surebets} color="emerald" />
        <StatCard icon={<GitMerge />} label="Pending Maps" value={stats.pending_mappings} color="red" />
        <StatCard icon={<Building2 />} label="Bookmakers" value={stats.bookmakers} color="blue" />
        <StatCard icon={<Users />} label="Users" value={stats.users} color="slate" />
        <StatCard icon={<TrendingUp />} label="History" value={stats.odds_history} color="slate" />
      </div>

      {/* Quick Link Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="quick-links">
        <Link to="/admin/data-quality" className="group p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-amber-500/30 transition-all">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-amber-400" />
            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400" />
          </div>
          <p className="text-lg font-bold text-slate-200 tabular-nums">{stats.health_score || '—'}%</p>
          <p className="text-[10px] text-slate-500">Data Quality</p>
        </Link>
        <Link to="/admin/mapping" className="group p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-cyan-500/30 transition-all">
          <div className="flex items-center justify-between mb-2">
            <GitMerge className="w-5 h-5 text-cyan-400" />
            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-cyan-400" />
          </div>
          <p className="text-lg font-bold text-slate-200 tabular-nums">{stats.pending_mappings}</p>
          <p className="text-[10px] text-slate-500">Pending Maps</p>
        </Link>
        <Link to="/admin/alerts" className="group p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-red-500/30 transition-all">
          <div className="flex items-center justify-between mb-2">
            <Bell className="w-5 h-5 text-red-400" />
            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-red-400" />
          </div>
          <p className="text-lg font-bold text-slate-200 tabular-nums">{stats.unread_alerts || 0}</p>
          <p className="text-[10px] text-slate-500">Alerts</p>
        </Link>
        <Link to="/admin/jobs" className="group p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-emerald-400" />
            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-emerald-400" />
          </div>
          <p className="text-xs font-bold text-slate-200">{stats.last_sync_time ? formatRelative(stats.last_sync_time) : 'N/A'}</p>
          <p className="text-[10px] text-slate-500">Last Sync</p>
        </Link>
      </div>

      {/* Cache Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Cache Stats
          </h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold text-blue-400 tabular-nums">{cache?.total_keys || 0}</p>
              <p className="text-xs text-slate-500">Total Keys</p>
            </div>
            <div>
              <p className="text-lg font-bold text-emerald-400 tabular-nums">{cache?.valid_keys || 0}</p>
              <p className="text-xs text-slate-500">Valid</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-400 tabular-nums">{cache?.expired_keys || 0}</p>
              <p className="text-xs text-slate-500">Expired</p>
            </div>
          </div>
        </div>

        {/* Recent Syncs */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Recent Syncs
          </h3>
          <div className="space-y-2">
            {recent_syncs?.slice(0, 3).map((sync, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Badge className={sync.status === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}>
                    {sync.status}
                  </Badge>
                  <span className="text-slate-400">{sync.provider_name}</span>
                </div>
                <div className="text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatRelative(sync.started_at)}
                </div>
              </div>
            ))}
            {!recent_syncs?.length && <p className="text-xs text-slate-500">No sync logs</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, color = 'slate' }) {
  const colors = {
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    slate: 'text-slate-400 bg-slate-800/50 border-slate-700',
  };
  const iconColor = { blue: 'text-blue-400', amber: 'text-amber-400', emerald: 'text-emerald-400', red: 'text-red-400', slate: 'text-slate-400' };

  return (
    <div className={`rounded-xl border p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-4 h-4 ${iconColor[color]}`}>{icon}</span>
        <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
